import { z } from 'zod';
import { LocaleSchema, TimestampSchema } from './primitives.js';

/**
 * `helpFeedback/{autoId}` — anonymous "Was this helpful?" votes from the
 * public /help operator manual (apps/marketing). Readers aren't signed in,
 * so the doc is created directly by the client SDK and
 * `infra/firestore.rules` re-validates every key/type/size server-side —
 * there is no callable in front of this write. Read by super-admin only;
 * never updated or deleted.
 */

export const HelpFeedbackSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9-]+$/)
    .describe('Help-article slug the vote is about (e.g. "queue-board").'),
  locale: LocaleSchema.describe('UI locale the reader had active when voting.'),
  helpful: z.boolean().describe('true = thumbs up, false = thumbs down.'),
  comment: z
    .string()
    .min(1)
    .max(500)
    .optional()
    .describe('Optional free-text note (offered on thumbs-down). Max 500 chars.'),
  createdAt: TimestampSchema.describe(
    'Server timestamp when the vote was stored (rules require request.time).',
  ),
});
export type HelpFeedback = z.infer<typeof HelpFeedbackSchema>;

/** Client-writable portion — `createdAt` is always serverTimestamp() at write. */
export const HelpFeedbackInputSchema = HelpFeedbackSchema.omit({ createdAt: true });
export type HelpFeedbackInput = z.infer<typeof HelpFeedbackInputSchema>;
